import clsx from 'clsx'
import { FileUpIcon } from 'lucide-react'
import React, { useContext } from 'react'

import { type IconProp } from 'slab/types'

import { FileUploadContext } from './context'

interface ContentProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'className'> {
  details?: React.ReactNode;
  icon?: IconProp | null;
  instructions?: React.ReactNode;
  layout?: 'horizontal' | 'vertical';
  size?: 'large' | 'small';
}

export function Content ({
  details,
  icon: Icon = FileUpIcon,
  instructions,
  layout = 'vertical',
  size = 'large',
  ...props
}: ContentProps) {
  const { dropping } = useContext(FileUploadContext)

  function renderInstructions () {
    if (instructions) return instructions

    if (dropping) {
      return <span>Drop to upload</span>
    }

    return (
      <>
        Drag and drop or{' '}
        <span className='text-thatch-600'>click here to upload</span>
      </>
    )
  }

  return (
    <div
      {...props}
      className={clsx('flex items-center h-full', {
        'px-4 py-10 gap-4': size === 'large',
        'p-3 gap-2': size === 'small',
        'flex-col': layout === 'vertical',
        'flex-row justify-between': layout === 'horizontal',
      })}
    >
      <div
        className={clsx('flex items-center', {
          'gap-4': size === 'large',
          'gap-2': size === 'small',
          'flex-col': layout === 'vertical',
          'flex-row': layout === 'horizontal',
        })}
      >
        {Icon && (
          <div
            className={clsx('transition', {
              'text-gray-500': !dropping,
              'text-thatch-500': dropping,
            })}
          >
            <Icon aria-hidden size={size === 'small' ? 16 : 24} />
          </div>
        )}
        <div className='block body-sm text-gray-800'>
          {renderInstructions()}
        </div>
      </div>
      {details && (
        <div
          className={clsx(
            'text-[0.625rem] leading-tight text-gray-500',
            {
              'text-right': layout === 'horizontal',
            }
          )}
        >
          {details}
        </div>
      )}
    </div>
  )
}

Content.displayName = 'FileUpload.Content'
